// Iter 65 — Drawer Links: map a name shown in the Compare Summary drawer to an existing, workspace-bound
// evidence route. Every link is a filtered search on a real route; when no safe destination exists the
// helpers return null and the drawer renders plain text. Pure functions.

const KINDS = new Set(["program", "physical-file", "logical-file", "display-file", "file", "object", "field", "screen", "evidence", "rule", "disposition"]);
const ENTITY_FOR_KIND = { program: "programs", "physical-file": "physical-files", "logical-file": "logical-files" };
const ENTITY_LABEL = { programs: "Programs list", "physical-files": "Physical files list", "logical-files": "Logical files list" };
const DISPOSITION_KINDS = new Set(["program", "physical-file"]);
const EVIDENCE_LABEL_RE = /^E\d+\s+·\s+([A-Z][A-Z0-9]*)\s+batch\s+([0-9a-f]{4,})/i;

const clean = (v) => (v === null || v === undefined ? "" : String(v).trim());

/** Split "LIB/NAME" (or "NAME") into { library, name }. */
export function splitQualified(text) {
  const t = clean(text);
  if (!t) return { library: null, name: null };
  const slash = t.indexOf("/");
  if (slash > 0 && slash < t.length - 1 && !t.includes("/", slash + 1)) return { library: t.slice(0, slash), name: t.slice(slash + 1) };
  return { library: null, name: t };
}

export function labelCompareDrawerItem(item) {
  if (!item) return "";
  if (item.label) return String(item.label);
  if (item.kind === "screen") return `${item.displayFile || "?"}/${item.format || "?"}`;
  if (item.library && item.name) return `${item.library}/${item.name}`;
  return clean(item.name);
}

function base(workspaceId) {
  return `/workspaces/${encodeURIComponent(workspaceId)}`;
}

function query(params) {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== "") p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** { to, destination } for a drawer item, or null when no safe, workspace-bound destination exists. */
export function getCompareDrawerLink(item, workspaceId) {
  const ws = clean(workspaceId);
  if (!item || !ws || !KINDS.has(item.kind)) return null;
  const name = clean(item.name);
  switch (item.kind) {
    case "program":
    case "physical-file":
    case "logical-file": {
      if (!name) return null;
      const entity = ENTITY_FOR_KIND[item.kind];
      return { to: `${base(ws)}/metadata${query({ entity, q: name })}`, destination: `${ENTITY_LABEL[entity]} filtered to ${name}` };
    }
    case "display-file":
      if (!name) return null;
      return { to: `${base(ws)}/metadata/screen-bindings${query({ display_file: name })}`, destination: `Screen format bindings for display file ${name}` };
    case "file":
    case "object": {
      if (!name) return null; // kind unknown → the impact graph handles any object, library-qualified
      const label = item.library ? `${item.library}/${name}` : name;
      return { to: `${base(ws)}/metadata/impact${query({ object: name, library: item.library || "" })}`, destination: `Impact graph for ${label}` };
    }
    case "field": {
      if (!name) return null;
      const scope = item.program ? ` in program ${item.program}` : item.file ? ` in file ${item.file}` : item.displayFile ? ` on display file ${item.displayFile}` : "";
      return {
        to: `${base(ws)}/metadata/field-dependencies${query({ field: name, program: item.program || "", file: item.file || "", display_file: item.displayFile || "" })}`,
        destination: `Field dependencies for ${name}${scope}`,
      };
    }
    case "screen": {
      const df = clean(item.displayFile);
      const fmt = clean(item.format);
      if (!df || df === "?" || !fmt || fmt === "?") return null; // unresolved display file → plain text
      return {
        to: `${base(ws)}/metadata/screen-bindings${query({ display_file: df, record_format: fmt, program_name: item.program || "" })}`,
        destination: `Screen format bindings for ${df}/${fmt}${item.program ? ` in ${item.program}` : ""}`,
      };
    }
    case "evidence": {
      if (item.batchId) return { to: `${base(ws)}/metadata/imports${query({ batch: item.batchId })}`, destination: `Import history batch ${clean(item.batchId).slice(0, 8)}` };
      if (item.command) return { to: `${base(ws)}/metadata/imports${query({ command: item.command })}`, destination: `Import history filtered to ${item.command}` };
      return null;
    }
    case "rule": {
      const program = clean(item.program);
      if (!program) return null;
      return { to: `${base(ws)}/business-rules${query({ program })}`, destination: `Business rules filtered to program ${program}` };
    }
    case "disposition": {
      if (!name || !item.state) return null;
      const kind = DISPOSITION_KINDS.has(item.objectKind) ? item.objectKind : "";
      return { to: `${base(ws)}/dispositions${query({ q: name, kind })}`, destination: `Disposition matrix filtered to ${name}` };
    }
    default:
      return null;
  }
}

export function canLinkCompareDrawerItem(item, workspaceId) {
  return getCompareDrawerLink(item, workspaceId) !== null;
}

/** Build the structured item behind one drawer name. ctx = { row, cell, sideKind, sideName, displayFile, batchId }. */
export function compareDrawerItem(name, ctx = {}) {
  const text = clean(name);
  if (!text) return null;
  const row = ctx.row;
  const cell = ctx.cell || "";
  if (row === "rules") {
    const m = text.match(/^(\S+)\s+L(\d+)$/);
    return { kind: "rule", name: text, program: m ? m[1] : null, line: m ? Number(m[2]) : null, label: text };
  }
  if (row === "callers" || row === "called") {
    const { library, name: obj } = splitQualified(text);
    return { kind: "program", library, name: obj, label: text };
  }
  if (row === "files") {
    const { library, name: obj } = splitQualified(text);
    return { kind: "file", library, name: obj, label: text };
  }
  if (row === "fields") {
    const sideOnly = cell === "left_only" || cell === "right_only";
    const program = sideOnly && ctx.sideKind === "program" ? ctx.sideName : null;
    const file = sideOnly && (ctx.sideKind === "physical-file" || ctx.sideKind === "logical-file") ? ctx.sideName : null;
    const displayFile = sideOnly && ctx.sideKind === "display-file" ? ctx.sideName : null;
    return { kind: "field", name: text, program, file, displayFile, label: text };
  }
  if (row === "screens") {
    const slash = text.indexOf("/");
    const displayFile = slash >= 0 ? text.slice(0, slash) : "?";
    const format = slash >= 0 ? text.slice(slash + 1) : text;
    const program = ctx.sideKind === "program" ? ctx.sideName : null;
    return { kind: "screen", displayFile, format, program, name: text, label: text };
  }
  if (row === "evidence") {
    const m = text.match(EVIDENCE_LABEL_RE);
    return { kind: "evidence", name: text, command: m ? m[1].toUpperCase() : null, batchId: ctx.batchId || null, label: text };
  }
  return { kind: "text", name: text, label: text }; // warnings and anything unknown stay plain
}

/** Iter 66 — an impact-graph node name ("LIB/OBJ" or "FILE.FIELD") with the graph's own kind → drawer item. */
export function impactDrawerItem(name, graphKind) {
  const text = clean(name);
  if (!text) return null;
  if (graphKind === "field") {
    const dot = text.indexOf(".");
    return dot > 0
      ? { kind: "field", name: text.slice(dot + 1), file: text.slice(0, dot), label: text }
      : { kind: "field", name: text, label: text };
  }
  const { library, name: obj } = splitQualified(text);
  const kind = graphKind === "program" || graphKind === "physical-file" || graphKind === "logical-file" || graphKind === "display-file" ? graphKind : "object";
  return { kind, library, name: obj, label: text };
}
